import { useMemo } from 'react';
import { gql } from '@soundxyz/gql-string';
import type { Maybe } from '@soundxyz/utils';
import type { FragmentType } from '@graphql/reactQuery';
import { DynamicFeePercentInfoFragmentDoc, getFragment } from '@graphql/reactQuery';

export type Percent = `${number}%`;

export const zeroPercent = '0%';

export const samFeePercent = '2.5%';
export const defaultFeePercent = '5%';

export const defaultFeePercentBPS = 500;

export function isValidPercent(str: string): str is Percent {
  return /^[0-9]+(\.[0-9]+)?%$/.test(str);
}

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment DynamicFeePercentInfo on Release {
    publicAffiliateFeePercent
    isFirstPhaseCompleted
    samConfig {
      id
      affiliateFeePercent
    }
  }
`);

export function useDynamicFeePercent({
  releaseInfo,
}: {
  releaseInfo: FragmentType<DynamicFeePercentInfoFragmentDoc>;
}) {
  const { publicAffiliateFeePercent, isFirstPhaseCompleted, samConfig } = getFragment(
    DynamicFeePercentInfoFragmentDoc,
    releaseInfo,
  );

  const samConfigAffiliateFeePercent = samConfig?.affiliateFeePercent;

  return useMemo(() => {
    if (isFirstPhaseCompleted && samConfigAffiliateFeePercent != null)
      return isValidPercent(samConfigAffiliateFeePercent)
        ? samConfigAffiliateFeePercent
        : zeroPercent;

    return publicAffiliateFeePercent && isValidPercent(publicAffiliateFeePercent)
      ? publicAffiliateFeePercent
      : zeroPercent;
  }, [isFirstPhaseCompleted, publicAffiliateFeePercent, samConfigAffiliateFeePercent]);
}

export const TWITTER_POST_SHARE_TEXT = (
  postWebappUri: string,
  artistTwitterHandle: Maybe<string>,
) => {
  if (!!artistTwitterHandle) {
    return `👀🎧 Check out this post on @soundxyz_ @${artistTwitterHandle} ${postWebappUri}` as const;
  }

  return `👀🎧 Check out this post on @soundxyz_ ${postWebappUri}` as const;
};

export const REGULAR_POST_NO_URL_SHARE_TEXT = `👀🎧 Check out this post on Sound.xyz`;

export const REGULAR_POST_SHARE_TEXT = (postWebappUri: string) =>
  `${REGULAR_POST_NO_URL_SHARE_TEXT} ${postWebappUri}` as const;

export function TwitterPostShareLink({
  webappUri,
  artistTwitterHandle,
}: {
  webappUri: string;
  artistTwitterHandle: Maybe<string>;
}) {
  return `https://twitter.com/intent/tweet?text=${encodeURIComponent(
    TWITTER_POST_SHARE_TEXT(webappUri, artistTwitterHandle),
  )}` as const;
}
